// Gentle, dismissible nudge shown only to guests who already have
// some local fasting data. Sits below the section strip. One-tap
// dismiss is permanent (per device).
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useIdentity } from "@/hooks/useIdentity";
import { FastLog } from "@/lib/fastLog";

const DISMISS_KEY = "monolith.nudge.linkAccount.dismissed";

export default function LinkAccountNudge() {
  const id = useIdentity();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [count, setCount] = useState<number>(() => FastLog.all().length);

  useEffect(() => {
    const handler = () => setCount(FastLog.all().length);
    window.addEventListener("monolith:log-changed", handler);
    return () => window.removeEventListener("monolith:log-changed", handler);
  }, []);

  // Suppress on the auth route itself.
  if (pathname.startsWith("/auth")) return null;
  if (id.loading || id.isAuthenticated) return null;
  if (dismissed) return null;
  if (count < 1) return null; // only nudge once they have history worth protecting

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div className="border-b border-[hsl(var(--panel-border))] bg-[hsl(var(--panel-inset))]/60 px-3 py-1.5 flex items-center justify-between gap-3 text-[10px] tracking-[0.18em] uppercase">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[hsl(var(--terminal-amber))] shrink-0">◇</span>
        <span className="text-[hsl(var(--terminal-dim))] truncate">
          {count} {count === 1 ? "FAST LOGGED" : "FASTS LOGGED"} · LINK A CALLSIGN TO SYNC ACROSS DEVICES
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/auth"
          className="text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--terminal-amber))]"
        >
          LINK ▸
        </Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-[hsl(var(--terminal-dim))] hover:text-[hsl(var(--foreground))] px-1"
          title="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
